import React, { useState, useContext, useEffect, useMemo } from "react";
import { getPaymentLink, deleteLinkPayment, getContactInfo } from "../service/api";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Menu, Input, Button, Table, Dropdown, Modal, Popover, Tag } from "antd";
import TableSkeleton from "../utils/script/tableSkeleton";
import { PaymentLinkContext } from "../../../context/PaymentLinkContext";
import "../utils/index.css";
import FilterPart from "./filterPart";
import DetailLinkModal from "../pages/detailsModal";
import SortAmoutPart from "./SortAmountPart";
import {
    SearchOutlined, SortDescendingOutlined, FilterOutlined, CheckOutlined, EyeOutlined,
    EditOutlined, LockOutlined, DeleteOutlined, EllipsisOutlined
} from "@ant-design/icons";
import toast from "react-hot-toast";
import { formattedDate, formatDatePaymentLink } from "../utils/script/indexUtils";
import TableContainer from "../../../common/table/TableContainer";

const MainPart = ({ businessAccountSelected, linksFiltered, setLinksFiltered }) => {

    // Session constants
    const currentUser = JSON.parse(sessionStorage.getItem('authUser'));
    const merchantId = currentUser?.user?.id || currentUser?.user?.merchantId;
    const token = currentUser?.token;

    // Context variables
    const { paymentLinks, setPaymentLinks } = useContext(PaymentLinkContext);
    const { contactList, setContactList } = useContext(PaymentLinkContext);
    const { constantPaymentLinks, setConstantPaymentLinks } = useContext(PaymentLinkContext);

    // Local state variables
    const [filteredLinks, setFilteredLinks] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [selectedLink, setSelectedLink] = useState(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [action, setAction] = useState();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [current, setCurrent] = useState('payment-link-item');
    const [filterModal, setFilterModal] = useState(false);
    const [sortModal, setSortModal] = useState(false);

    const deleteLinkMutation = useMutation(deleteLinkPayment);
    const queryClient = useQueryClient();

    useEffect(() => {
        setFilteredLinks(paymentLinks ? paymentLinks : []);
    }, [paymentLinks]);

    const { data: paymentLinkList, isLoading, error: errorPaymentLinks } = useQuery({
        queryKey: ["paymentLinks", businessAccountSelected, token],
        queryFn: () =>
            getPaymentLink({
                selectedBusiness: businessAccountSelected,
                token,
            }),
        enabled: !!token && !!businessAccountSelected
    });

    const { data: contactInfoList, isLoading: isLaodingContact, error: errorContact } = useQuery({
        queryKey: ["contactInfos", merchantId, token],
        queryFn: getContactInfo,
        enabled: !!merchantId
    });

    useEffect(() => {
        setPaymentLinks(paymentLinkList);
    }, [paymentLinkList]);

    useEffect(() => {
        setContactList(contactInfoList);
    }, [contactInfoList]);

    const itemsMenu = [
         {
            label: <div className="liste-table d-flex align-items-center gap-2">
                <span className="mb-2">Liste des liens</span>
                <span className="badge-count mb-2">{filteredLinks?.length || 0}</span>
            </div>,
            key: 'payment-link-item'
        },
        {
            label: <div className="liste-table d-flex align-items-center gap-2">
                <span className="mb-0">Liste des contacts</span>
                <span className="badge-count">{contactList?.length || 0}</span>
            </div>,
            key: 'contact-item'
        }
    ];




    const handleSearch = (e) => {
        setSearchText(e.target.value.toLowerCase());
        const filteredData = paymentLinks.filter((item) => Object.values(item).some((val) => String(val).toLowerCase().includes(e.target.value.toLowerCase())));
        setLinksFiltered(filteredData);
    }

    const showDetailsModal = (record) => {
        setSelectedLink(record);
        setIsDetailsModalOpen(true);
    }

    const showModifyModal = (record) => {
        setSelectedLink(record);
        setAction("Modifier");
        setIsModalOpen(true);
    }

    const deletedPaymentLink = (deletedLink) => {
        const data = linksFiltered.filter((item) => item.id !== deletedLink.id);
        setLinksFiltered(data);
    }

    const deletePaymentLink = (record) => {
        const requestData = {
            id: record.id,
            token: currentUser ? currentUser.token : null,
            business: businessAccountSelected?.id
        }

        deleteLinkMutation.mutate(requestData, {
            onSuccess: (response) => {
                toast.success("Lien de paiement supprimé.");
                queryClient.invalidateQueries({
                    queryKey: ["paymentLinks", businessAccountSelected, currentUser ? currentUser.token : null]
                });
            },
            onError: (error) => {
                console.error(error);
            }
        })
    }

    const confirmDeleteModal = (record) => {
        Modal.confirm({
            title: "Confirmer la suppression",
            content: "Êtes vous sûr ? Cette action est irreversible.",
            okText: "Supprimer",
            cancelText: "Annuler",
            onOk() {
                deletePaymentLink(record);
            },
            onCancel() {
                // Faire quelque chose
            }
        });
    }

    // Le gestionnaire du menu des types
    const onClickMenu = (e) => {
        setCurrent(e.key);
    }

    const contactColumns = [
        {
            title: "Nom complet",
            dataIndex: "fullName",
            key: "fullName",
            render: (fullName) => <span className="normal-text">{fullName || 'Non défini'}</span>
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            render: (email) => <span className="normal-text">{email}</span>
        },
        {
            title: "Montant total",
            dataIndex: "totalPaid",
            key: "totalPaid",
            align: "right",
            render: (totalPaid) => {
                const formattedAmount = new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: 'XOF'
                }).format(totalPaid);
                return <span className="normal-text">{formattedAmount}</span>
            }
        },
    ]
    const columns = [
        {
            Header: "Description du lien",
            accessor: "description",
            filterable: false,
        },
        {
            Header: "Date de création",
            accessor: "startDate",
            filterable: false,
            Cell: ({ value }) => {
                return value ?
                    <span className="namesTrans" >
                        {new Date(value).toLocaleString("fr-FR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </span> :
                    <span> Non défini </span>
            }
        },
        {
            Header: "Destinataire",
            accessor: "fullName",
            filterable: false,
            Cell: ({ value }) => <span>{value || "Non défini"}</span>
        },
        {
            Header: "Montant",
            accessor: "amount",
            filterable: "right",
            Cell: ({ value }) => {
                const formattedAmount = new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: 'XOF'
                }).format(value);
                return <span>{formattedAmount}</span>
            }
        },
        {
            Header: "Statut",
            accessor: "active",
            filterable: false,
            Cell: ({ value }) => {
                if (value == false) {
                    return (
                        <div className="status-active">
                            <CheckOutlined className="status-icon" />
                            <span> Payé </span>
                        </div>)
                } else {
                    return (
                        <div className="status-blocked">
                            <span> Non payé</span>
                        </div>)
                }

            },
        },
        {
            Header: "Action",
            accessor: "action",
            Cell: ({ row }) => {
                const record = row.original;
                return (<Dropdown
                    overlay={
                        <Menu>
                            <Menu.Item key="1" icon={<EyeOutlined />} onClick={() => showDetailsModal(record)}>
                                Détails du lien
                            </Menu.Item>
                            {/* <Menu.Item key="2" icon={<EditOutlined />} onClick={() => showModifyModal(record)}>
                                Modifier
                            </Menu.Item> */}
                            <Menu.Item key="4" icon={<DeleteOutlined />} onClick={() => confirmDeleteModal(record)} danger disabled={record.paymentStatus == "SUCCESS" ? true : false}>
                                Supprimer
                            </Menu.Item>
                        </Menu>
                    }
                    trigger={["click"]}
                >
                    <div className="action-menu-trigger">
                        <EllipsisOutlined />
                    </div>
                </Dropdown>);
            },
        },
    ];

    return (
        <div className="mt-3">
            <Menu onClick={onClickMenu} selectedKeys={[current]} mode="horizontal" items={itemsMenu} className="mb-3" />

            {
                current == 'payment-link-item' && filteredLinks && <TableContainer
                    columns={columns}
                    data={filteredLinks || []}
                    isGlobalSearch={true}
                    isGlobalFilter={true}
                    customPageSize={10}
                    className="custom-header-css"
                    divClass="table-responsive table-card mb-3"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light"
                    SearchPlaceholder="Recherche..."
                    isLoading={isLoading}
                    isError={errorPaymentLinks}

                >
                    <div className="row g-2">
                        <div className="col-6 col-md-4 col-lg-3">
                            <Button
                                size="medium"
                                icon={<FilterOutlined />}
                                color="black"
                                variant="outlined"
                                onClick={() => setFilterModal(!filterModal)}
                                className="w-100"
                            >
                                Filtrer
                            </Button>
                        </div>

                        <div className="col-6 col-md-4 col-lg-3">
                            <Button
                                size="medium"
                                icon={<SortDescendingOutlined />}
                                color="black"
                                variant="outlined"
                                onClick={() => setSortModal(!sortModal)}
                                className="w-100"
                            >
                                Trier
                            </Button>
                        </div>
                    </div>

                </TableContainer>
            }
            {
                current == 'contact-item' && <Table columns={contactColumns} dataSource={contactList} loading={isLoading} pagination={true} scroll={{ x: true }} className="mt-3" />
            }
            <Modal
                title="Filtrer"
                open={filterModal}
                onOk={() => setFilterModal(false)}
                onCancel={() => setFilterModal(false)}
                className="filter-modal-payment-link"
                width={300}
                footer={null}
            >
                <FilterPart showFilterModal={setFilterModal} setFilteredLinks={setFilteredLinks} />
            </Modal>
            <Modal title="Trier par montant" open={sortModal} onCancel={() => setSortModal(false)} footer={null} width={200}>
                <SortAmoutPart setFilteredLinks={setFilteredLinks} showSortModal={setSortModal} />
            </Modal>
            <Modal open={isDetailsModalOpen} onCancel={() => setIsDetailsModalOpen(false)} width={650} footer={null}>
                <DetailLinkModal linkSelected={selectedLink} setModalDetail={setIsDetailsModalOpen} />
            </Modal>
        </div>
    )
}

export default MainPart;